import type { PayloadAction } from '@reduxjs/toolkit';
import type { PersonInformation } from '@/types/entities/entityInfo';
import type { CreateInsuredOptions } from '@/domains/insureds/sagas/types/CreateInsuredOptions';
import type { SelectOption } from '@/features/editMode/types/selectOption';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import type { RootState } from '@/store/types/RootState';
import type { CreateInsuredDetails } from '../store/types/createInsuredTypes';
import type { CreateInsuredAutomaticallyBodyType, CreateInsuredManuallyBodyType } from './types';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsSelectPropertyNames } from '@/features/eds/enums/EdsSelectPropertyNames';
import { AddInsuredModalSteps } from '@/features/serviceModal/components/AddInsuredModal/enums';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { getEDSClaimSetSaga } from '@/domains/claimSets/sagas/claimSetWatcher';
import { getClaimSetV2 } from '@/domains/claimSets/store/claimSetSlice';
import InsuredEvents from '@/domains/insureds/events';
import { CacheVariables } from '@/enums/CacheVariables';
import CacheService from '@/services/CacheService/CacheService';
import EventService from '@/services/EventService/EventService';
import FormService from '@/services/FormService/FormService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';
import {
  createInsured,
  getCreateInsuredExistingUserById,
  getCreateInsuredOptions,
  getCreateInsuredOptionsSuccess,
  resetCreateInsuredState,
  setCreateInsuredOptionsStatus,
  setCreateInsuredStatus,
  updateCreateInsuredStepValues,
} from '../store/createInsuredSlice';

function* createInsuredSaga({
  payload,
}: PayloadAction<CreateInsuredManuallyBodyType | CreateInsuredAutomaticallyBodyType>) {
  try {
    const currentClaimSetId = yield select((state: RootState) => state.claimSets.currentClaimSetId);

    const { data } = yield call(HttpService.post, '/examiner/insureds/create-insured', {
      ...payload,
      claimSetId: currentClaimSetId,
    });

    SnacksService.add({
      type: SnackType.Success,
      title: data,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });

    yield* getEDSClaimSetSaga(getClaimSetV2(`${currentClaimSetId}`));
    yield delay(300);
    EventService.dispatch(new InsuredEvents.Created({}));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateInsuredStatus(false));
    yield put(resetCreateInsuredState());
    ModalService.close(Modals.AddInsured);
  }
}

function* getCreateInsuredOptionsSaga() {
  try {
    let createInsuredOptions = CacheService.get<CreateInsuredOptions>(CacheVariables.CreateInsuredOptions, false, 1);

    if (!createInsuredOptions) {
      const [
        { data: genderOptions },
        { data: martialStatusOptions },
        { data: occupationOptions },
        { data: documentDeliveryPreferenceOptions },
        { data: contactPreferenceOptions },
        { data: prefixOptions },
      ]: [
        HttpServiceResponse<SelectOption[]>,
        HttpServiceResponse<SelectOption[]>,
        HttpServiceResponse<SelectOption[]>,
        HttpServiceResponse<SelectOption[]>,
        HttpServiceResponse<SelectOption[]>,
        HttpServiceResponse<SelectOption[]>,
      ] = yield all([
        call(HttpService.get, '/examiner/update-options/gender-options'),
        call(HttpService.get, '/examiner/update-options/marital-status-options'),
        call(HttpService.get, '/examiner/update-options/occupation-options'),
        call(HttpService.get, '/examiner/update-options/document-delivery-preference-options'),
        call(HttpService.get, '/examiner/update-options/contact-preference-options'),
        call(
          HttpService.get,
          `options/${EdsEntityCodes.Person}/${EdsSelectPropertyNames.Prefix}`,
          {},
          { baseURL: import.meta.env.VITE_API_URL_EDS },
        ),
      ]);

      const optionsRecord = {
        genderOptions,
        martialStatusOptions,
        occupationOptions,
        documentDeliveryPreferenceOptions,
        contactPreferenceOptions,
        prefixOptions,
      };

      CacheService.set<CreateInsuredOptions>(
        CacheVariables.CreateInsuredOptions,
        optionsRecord,
        CacheService.minutes(10),
        1,
      );
      createInsuredOptions = optionsRecord;
    }

    const updatedPrefixOptions = FormService.prependEmptyOption(
      createInsuredOptions.prefixOptions[EdsSelectPropertyNames.Prefix],
    );

    yield put(
      getCreateInsuredOptionsSuccess({
        genders: createInsuredOptions.genderOptions,
        martialStatuses: createInsuredOptions.martialStatusOptions,
        occupations: createInsuredOptions.occupationOptions,
        documentPreferences: createInsuredOptions.documentDeliveryPreferenceOptions,
        contactPreferences: createInsuredOptions.contactPreferenceOptions,
        prefixes: updatedPrefixOptions,
      }),
    );
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateInsuredOptionsStatus(false));
  }
}

function* fetchCreateInsuredExistingUserByIdSaga({ payload }: PayloadAction<string>) {
  try {
    const { data }: HttpServiceResponse<Array<PersonInformation>> = yield call(
      HttpService.get,
      `/examiner/search/person?searchExpression=${payload}`,
    );

    const insuredDetailsFormData: CreateInsuredDetails = yield select(
      (state: RootState) => state.claimSetCreation.form[AddInsuredModalSteps.InsuredDetails],
    );
    yield put(
      updateCreateInsuredStepValues({
        step: AddInsuredModalSteps.InsuredDetails,
        value: {
          ...insuredDetailsFormData,
          isSsnValid: !data.length,
          ssnValidationResult: data,
        },
      }),
    );
  } catch (error) {
    console.error(error);
  }
}

export function* createInsuredWatcher() {
  yield takeLatest(getCreateInsuredOptions.type, getCreateInsuredOptionsSaga);
  yield takeLatest(getCreateInsuredExistingUserById.type, fetchCreateInsuredExistingUserByIdSaga);
  yield takeLatest(createInsured.type, createInsuredSaga);
}
